import { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { LoadingText, Muted, Row, Section, SectionTitle } from '../../../components/Detail/DetailParts';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useSessionDetail from '../../../hooks/dashboard/sessions/useSessionDetail';
import { KIND_LABELS } from '../../../models/profit/profit.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';
import { TransactionDetailBody } from '../Transactions/TransactionDetailSlider';

const money = (value: string | number) => formatNaira(Number(value));

// One conversation's timeline: model calls, billed WhatsApp messages,
// purchases with their margin and fees (limits spec P5/P6). No message text:
// that lives only in LangSmith (invariant 5).
const SessionDetailSlider: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const { session, loading, fetchSession } = useSessionDetail();
  const [purchase, setPurchase] = useState<string | null>(null);

  useEffect(() => {
    if (data?.id) fetchSession(data.id);
    // eslint-disable-next-line
  }, [data?.id]);

  return (
    <TableSliderContentWrapper label='Session'>
      {loading || !session ? (
        <LoadingText>Loading…</LoadingText>
      ) : purchase ? (
        <>
          <span>
            <PrimaryWhiteButton type='button' onClick={() => setPurchase(null)}>
              ← Back to the session
            </PrimaryWhiteButton>
          </span>
          <TransactionDetailBody reference={purchase} />
        </>
      ) : (
        <>
          <Section>
            <Profit className={Number(session.profit) < 0 ? 'loss' : 'gain'}>{money(session.profit)}</Profit>
            <Muted>
              {session.user_name ?? session.chat_id} · started {tableFormatDateTimeNG(session.started_at)}
              {session.ended_at ? ` · ended ${tableFormatDateTimeNG(session.ended_at)}` : ' · still open'}
            </Muted>
            {session.langsmith_url && (
              <span>
                <PrimaryWhiteButton type='button' onClick={() => window.open(session.langsmith_url!, '_blank', 'noopener')}>
                  Open in LangSmith
                </PrimaryWhiteButton>
              </span>
            )}
          </Section>

          <Section>
            <SectionTitle>Totals</SectionTitle>
            <Row label='Messages in / out'>
              {session.messages_in} / {session.messages_out}
            </Row>
            <Row label='Claude calls'>
              {session.llm_calls} · {money(session.llm_cost)}
            </Row>
            <Row label='WhatsApp cost'>{money(session.whatsapp_cost)}</Row>
            <Row label='Paystack fees'>{money(session.paystack_fees)}</Row>
            <Row label='Revenue'>{money(session.revenue)}</Row>
          </Section>

          {session.purchase_list.length > 0 && (
            <Section>
              <SectionTitle>Purchases</SectionTitle>
              {session.purchase_list.map((p) => (
                <PurchaseRow key={p.id} type='button' onClick={() => setPurchase(p.reference)}>
                  <span>
                    {money(p.amount)} {p.service_id}
                  </span>
                  <span>{p.status}</span>
                  <span>{p.profit_loss != null ? money(p.profit_loss) : '—'}</span>
                </PurchaseRow>
              ))}
            </Section>
          )}

          <Section>
            <SectionTitle>Timeline</SectionTitle>
            {session.timeline.map((e, i) => (
              <Event key={i}>
                <span>{KIND_LABELS[e.kind] ?? e.kind}</span>
                <span>{describe(e.kind, e.details)}</span>
                <span className={Number(e.revenue) - Number(e.cost) < 0 ? 'loss' : 'gain'}>
                  {Number(e.revenue) ? `+${money(e.revenue)}` : `−${money(e.cost)}`}
                </span>
                <Muted>{tableFormatDateTimeNG(e.at)}</Muted>
              </Event>
            ))}
            {!session.timeline.length && <Muted>No costs recorded yet.</Muted>}
          </Section>
        </>
      )}
    </TableSliderContentWrapper>
  );
};

export default SessionDetailSlider;

const describe = (kind: string, details: Record<string, any>): string => {
  if (kind === 'LLM_CALL') {
    const tokens = (details.uncached_input ?? 0) + (details.cache_read ?? 0) + (details.cache_write ?? 0);
    return `${details.model ?? ''} · ${tokens.toLocaleString()} in (${(details.cache_read ?? 0).toLocaleString()} cached) / ${(details.output ?? 0).toLocaleString()} out`;
  }
  if (kind === 'WHATSAPP_MESSAGE') return `${details.category ?? ''} message`;
  if (kind === 'PAYSTACK_FEE') return details.source === 'paystack' ? 'as reported by Paystack' : 'estimated';
  if (kind === 'PURCHASE_MARGIN') return details.service_id ?? '';
  return '';
};

const Profit = styled.div`
  font-size: 24px;
  font-weight: 700;
  &.loss {
    color: ${({ theme }) => theme.palette.errorRed};
  }
  &.gain {
    color: ${({ theme }) => theme.palette.greenColor};
  }
`;

const Event = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.6fr auto;
  gap: 2px 8px;
  font-size: 13px;
  padding: 6px 0;
  border-bottom: 1px solid #efece4;

  & > span:nth-of-type(3) {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  & > span.loss {
    color: ${({ theme }) => theme.palette.errorRed};
  }
  & > span.gain {
    color: ${({ theme }) => theme.palette.greenColor};
  }
  & > div {
    grid-column: 1 / -1;
  }
`;

const PurchaseRow = styled.button`
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  gap: 8px;
  text-align: left;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  padding: 10px 12px;
  background: white;
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  & > span:last-of-type {
    text-align: right;
  }
`;
